import type { FC, ReactNode } from 'react'
import type { DoorStyle } from '@/types'
import type { DoorFilters } from '@/components/widgets/door-gallery'

import { useState } from 'react'
import { Thermometer } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { SectionHeading } from '@/components/generals/section-heading'
import { Reveal } from '@/components/motion/reveal'
import { DoorGallery, EMPTY_DOOR_FILTERS } from '@/components/widgets/door-gallery'
import { FinancingSection } from '@/components/landing/sections/financing-section'
import { DoorProcess, DoorStyleIndex, RValueChart } from '@/components/landing/doors/door-sections'
import { DoorsHero } from '@/components/landing/doors/doors-hero'
import { QUOTE_PATH } from '@/constants/site'

const HELP_ME_CHOOSE = `${QUOTE_PATH}?service=new-doors&entry=help_me_choose`

const NewDoorsPage: FC = (): ReactNode => {
    const [filters, setFilters] = useState<DoorFilters>(EMPTY_DOOR_FILTERS)

    const selectStyle = (style: DoorStyle) => {
        setFilters(current => ({ ...EMPTY_DOOR_FILTERS, style: current.style === style ? null : style }))
        document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <Layout>
            <Seo
                title='New doors'
                description="Traditional, carriage house, modern and full-view glass garage doors for Riverton metro homes, each shown with its installed price range."
            />

            <DoorsHero galleryHref='#gallery' helpHref={HELP_ME_CHOOSE} />

            <section id='styles' className='pb-20 pt-8 md:pb-28'>
                <Container>
                    <SectionHeading
                        eyebrow="By style"
                        title="Match the door to the house you have."
                        description="Choose a style and the gallery below narrows to match. Every price includes installation and taking your old door away."
                    />

                    <div className='mt-12'>
                        <DoorStyleIndex activeStyle={filters.style} onSelect={selectStyle} />
                    </div>
                </Container>
            </section>

            <section id='gallery' className='scroll-mt-24 bg-paper py-20 md:py-28'>
                <Container>
                    <SectionHeading
                        eyebrow="Every model"
                        title="All our doors, each priced installed."
                        description="Narrow the list by style, material and color. Shortlist up to 4 doors to compare, and we'll quote every one of them in a single visit."
                        action={(
                            <div className='flex flex-wrap items-center gap-4'>
                                <span className='text-[15px] text-slate'>Still undecided?</span>
                                <ButtonLink href={HELP_ME_CHOOSE} variant='outline'>Get three suggestions</ButtonLink>
                            </div>
                        )}
                    />

                    <div className='mt-12'>
                        <DoorGallery filters={filters} onFiltersChange={setFilters} />
                    </div>
                </Container>
            </section>

            <section id='how-it-works' className='py-24 md:py-32'>
                <Container>
                    <SectionHeading
                        eyebrow="The process"
                        title="From the gallery to your driveway."
                        description="Most doors are here in 1–3 weeks. Install day takes around 5 hours, and that includes taking out the old door."
                    />

                    <div className='mt-16 md:mt-20'>
                        <DoorProcess />
                    </div>
                </Container>
            </section>

            <section id='r-value' className='pb-24 md:pb-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-4'>
                        <div className='lg:sticky lg:top-32'>
                            <SectionHeading
                                layout='stack'
                                eyebrow="Keeping heat in"
                                title="R-value in plain terms."
                                titleClassName='max-w-[10ch]'
                                description="The higher the R-value, the better a door holds heat. An uninsulated door is R-0 and R-9 is good. For an attached garage with a room above, aim for R-16 or more."
                            />

                            <Reveal className='mt-8'>
                                <p className='flex items-start gap-3 rounded-[22px] bg-sand/70 p-5 text-[14.5px] leading-relaxed text-ink'>
                                    <Thermometer className='mt-0.5 size-5 shrink-0 text-ember' aria-hidden='true' />
                                    <span>Only one model reaches R-16: the Norrland Flush. That is the rating we recommend when a room sits above an attached garage.</span>
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <Reveal className='lg:col-span-8' y={50}>
                        <RValueChart />
                    </Reveal>
                </Container>
            </section>

            <FinancingSection
                title="A new door from $49 a month."
                description="Pay for a new door over 12–60 months, with 0% APR for 12 months on approved credit. Checking your rate won't affect your credit score."
                showSteps
            />

            <CtaBand
                eyebrow="Still deciding"
                title="Let us narrow it down to three."
                body="Send a few details about your home and we'll come back with three doors that suit it."
                primary={{ label: "Suggest three doors", href: HELP_ME_CHOOSE }}
            />
        </Layout>
    )
}

export default NewDoorsPage
